import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addBreadcrumb, captureException } from 'error_tracker';
import ApiRequest from 'core/api/request';
import { parseError } from 'core/api/utils';
import { promiser } from 'core/utils';
import CartContext from 'core/providers/CartContext';
// NOTE: This cannot import the MpOrderContext (see CheckoutEntryContainer)

const defaultProps = {
	clientKey: null, // key used to setup stripe card (only set when we should use "new" stripe elements)
	nonStripe: true, // True when stripe is not the processor
	stripePublicKey: null, // key used to load stripe-js
	storeId: null, // Id of store in single-seller checkout
	testMode: false, // True when every active cart's store is in test mode
	complete: false, // True, setup complete. Can be sure we have fetched the info
};

const ProcessorContext = createContext({ ...defaultProps });
ProcessorContext.displayName = 'ProcessorContext';

export const ProcessorProvider = ({ children, value }) => {
	const { loading, getActiveCarts } = useContext(CartContext);

	const [ processorInfo, setProcessorInfo ] = useState({
		...defaultProps,
		...value,
	});
	const {
		clientSecret,
		nonStripe,
		stripePublicKey,
		complete,
	} = processorInfo;

	const activeCarts = getActiveCarts();
	const storeId = activeCarts?.length === 1 ? activeCarts[0].storeId : null;
	const testMode = activeCarts.every(cart => cart.store?.testMode);

	const getProcessorInfo = useCallback(async (refresh = false) => {
		const params = {};
		if (testMode) params.test_mode = "t";
		if (refresh) params.refresh = "t";

		const endpoint = storeId && !refresh ? `/processor_info/${storeId}/` : `/processor_info/`;
		const [err, resp] = await promiser(ApiRequest.get(endpoint, params));

		if (err) {
			console.error(err);
			captureException(err);
			return;
		}

		let { clientSecret, processor } = resp || {};
		const nonStripe = processor !== "stripe";
		if (nonStripe) clientSecret = null; // dont need stripe key
		const stripePublicKey = testMode ? window?.cj?.stripe_test_pkey : window?.cj?.stripe_pkey;
		setProcessorInfo({
			clientSecret,
			nonStripe,
			stripePublicKey,
			storeId,
			testMode,
			complete: true,
		});
	}, [storeId, testMode]);

	const fetchCardPreview = useCallback(async () => {
		const params = testMode ? { test_mode: "t" } : {};
		const [err, cardInfo] = await promiser(ApiRequest.get(`/processor_info/card_preview/`, params));
		if (err) {
			addBreadcrumb({
				category: 'cart_checkout',
				message: 'ProcessorContext: Not able to fetch card preview',
				data: { err },
			});
			return;
		}

		return cardInfo;
	}, [testMode]);

	useEffect(() => {
		if (complete || loading) return;
		// Fetch the processor info:
		getProcessorInfo();
	}, [complete, loading, storeId, testMode]);

	const sharedValue = {
		clientSecret,
		nonStripe,
		stripePublicKey,
		storeId,
		testMode,
		complete,
		fetchCardPreview,
		getProcessorInfo,
	};

	return (
		<ProcessorContext.Provider value={sharedValue}>
			{children}
		</ProcessorContext.Provider>
	);
};

ProcessorProvider.sharedPropTypes = {
	clientKey: PropTypes.string,
	stripePublicKey: PropTypes.string,
	nonStripe: PropTypes.bool,
	complete: PropTypes.bool,
	storeId: PropTypes.number,
	testMode: PropTypes.bool,
};

ProcessorProvider.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.shape({
		...ProcessorProvider.sharedPropTypes,
	}),
};

ProcessorProvider.defaultProps = {
	value: {
		...defaultProps,
	},
};

export default ProcessorContext;
